import { Card, Group, Stack, Text } from '@mantine/core'
import { Badge } from 'components/Badge'
import { format } from 'util/date-fns'
import { UserThumbnail } from 'modules/users/components'
import { ShiftNode } from '../types.graphql'
import { parseLocation, parseShiftRole } from '../util'
import { useIsMobile } from 'util/hooks'
import { capitalizeFirstLetter } from 'util/parsing'
import { createStyles } from '@mantine/emotion'

interface UserShiftCardProps {
  shift: Pick<
    ShiftNode,
    'id' | 'location' | 'datetimeStart' | 'filledSlots' | 'datetimeEnd' | 'name'
  >
  year?: boolean
}

export const UserShiftCard: React.FC<UserShiftCardProps> = ({
  shift,
  year,
}) => {
  const { classes } = useStyles()
  const isMobile = useIsMobile()
  return (
    <Card withBorder className={classes.card} shadow="md">
      <Group justify={'space-between'} mb={'md'}>
        <Stack align={'flex-start'} gap={0}>
          <Badge variant={'filled'} radius={'sm'} color="samfundet-red">
            {shift.name}
          </Badge>
          <Text fw={'thin'} c={'dimmed'}>
            {capitalizeFirstLetter(
              format(
                new Date(shift.datetimeStart),
                `cccc d. MMMM ${year ? 'yyyy' : ''}`
              )
            )}
          </Text>
        </Stack>
        <Stack gap={0} align={'flex-end'}>
          {shift.location && (
            <Badge>{parseLocation(shift.location).name}</Badge>
          )}
          <Text ta={'center'}>
            {format(new Date(shift.datetimeStart), 'HH:mm')} -{' '}
            {format(new Date(shift.datetimeEnd), 'HH:mm')}
          </Text>
        </Stack>
      </Group>
      <Group wrap="nowrap">
        {shift.filledSlots.map(slot => (
          <Stack key={slot.id} gap="xs" align={'center'}>
            <UserThumbnail
              user={slot.user}
              size={isMobile ? 'md' : 'lg'}
              radius={100}
            />
            <Badge variant={'outline'} size={isMobile ? 'sm' : 'sm'} my={0}>
              {parseShiftRole(slot.role)}
            </Badge>
            <Text my={0} size={isMobile ? 'sm' : 'sm'}>
              {slot.user.firstName}
            </Text>
          </Stack>
        ))}
      </Group>
    </Card>
  )
}

const useStyles = createStyles({
  title: {
    color: 'var(--mantine-color-gray-6)',
    fontWeight: 'bold',
  },
  card: {
    borderTop: '4px solid var(--mantine-color-samfundet-red-0)',
    margin: 'var(--mantine-spacing-sm) 0',
    maxWidth: 700,
    overflowX: 'scroll',
  },
})
